import React, { useEffect, useState } from 'react';
import { RepairService, Model } from '../types/BookingTypes';
import { useCart } from '../context/CartContext';
import ServiceDetailsModal from './ServiceDetailsModal';
import ReviewList from './ReviewList';
import { ModelReview, getModelReviews } from '../services/orderService';
import { isLoggedIn } from '../services/authService';
import { useNavigate, useLocation } from 'react-router-dom';

interface ServiceSelectorProps {
  services: RepairService[];
  selectedServices: RepairService[];
  onSelect: (services: RepairService[]) => void;
  onBack?: () => void;
  modelName?: string;
  model: Model;
}

const ServiceSelector: React.FC<ServiceSelectorProps> = ({ 
  services, 
  selectedServices, 
  onSelect,
  onBack,
  modelName,
  model
}) => {
  const { addToCart, removeFromCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<RepairService | null>(null);
  const [reviews, setReviews] = useState<ModelReview[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);


  useEffect(() => {
    const fetchReviews = async () => {
      if (model.id) {
        setIsLoadingReviews(true);
        try {
          const modelReviews = await getModelReviews(model.id);
          setReviews(modelReviews);
        } catch (error) {
          console.error('Error fetching reviews:', error);
        } finally {
          setIsLoadingReviews(false);
        }
      }
    };

    fetchReviews();
  }, [model.id]);

  const handleAuthenticationCheck = () => {
    if (!isLoggedIn()) {
      // Store current location to redirect back after login
      navigate('/login', { 
        state: { 
          from: location.pathname,
          model: model,
          selectedServices: selectedServices
        } 
      });
      return false;
    }
    return true;
  };

  const handleToggle = (service: RepairService) => {
    // Check authentication first
    if (!handleAuthenticationCheck()) {
      return;
    }

    const isSelected = selectedServices.some(s => s.id === service.id);
    const newServices = isSelected 
      ? selectedServices.filter(s => s.id !== service.id)
      : [...selectedServices, service];
    
    // Update local state immediately for responsive UI
    onSelect(newServices);
    
    // Fire and forget - instant operations
    if (isSelected) {
      // Remove from cart using optimistic removal
      removeFromCart(model.id, service.id);
    } else {
      // Add to cart - only add the new service
      addToCart(model, [service]);
    }
  };

  const handleServiceClick = (service: RepairService) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Services Section */}
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              {onBack && (
                <button
                  onClick={onBack}
                  className="mr-3 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white shadow-sm hover:shadow-md active:scale-95 transition-all"
                >
                  <i className="fas fa-arrow-left text-gray-600 text-sm"></i>
                </button>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 bg-minto-500 rounded-full" />
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">
                    Select Services
                  </h3>
                </div>
                <p className="text-xs md:text-sm text-gray-500 ml-3">{modelName}</p>
              </div>
            </div>
            <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full shadow-sm">{services.length} services</span>
          </div>

          {/* Services List */}
          <div className="space-y-3 md:space-y-4">
            {services.map((service) => {
              const isSelected = selectedServices.some(s => s.id === service.id);
              return (
                <div
                  key={service.id}
                  className={`relative w-full flex flex-col rounded-lg md:rounded-xl overflow-hidden transition-all duration-300 ${
                    isSelected
                      ? 'bg-white border-2 border-minto-500 shadow-lg shadow-minto-100'
                      : 'bg-white border border-gray-100 shadow-md hover:shadow-lg hover:-translate-y-0.5'
                  }`}
                >
                  {/* Selected badge */}
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-6 h-6 bg-minto-500 rounded-full flex items-center justify-center">
                      <i className="fas fa-check text-white text-xs"></i>
                    </div>
                  )}

                  {/* Main Content */}
                  <div className="p-4 md:p-5 flex items-start">
                    <div className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 bg-gray-50 rounded-xl p-2 mr-3 md:mr-4 flex items-center justify-center">
                      <img
                        src={service.serviceImage}
                        alt={service.serviceName}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm md:text-base mb-1 line-clamp-1">{service.serviceName}</h4>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <i className="fas fa-clock text-[10px]"></i>
                        {service.serviceDuration} mins
                      </span>
                    </div>
                    <div className="flex-shrink-0 text-right ml-3">
                      <span className="text-lg md:text-xl font-bold text-gray-900">₹{Math.round(service.finalPrice || (service.actualPrice + (service.serviceCost || 0) + ((service.serviceCost || 0) * 0.18)))}</span>
                      {service.markedPrice > 0 && service.markedPrice !== service.actualPrice && (
                        <div className="text-xs text-gray-400 line-through">₹{Math.round(service.markedPrice)}</div>
                      )}
                    </div>
                  </div>

                  {/* Warranty Info */}
                  <div className="mx-4 md:mx-5 mb-3 px-3 py-2 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <i className="fas fa-shield-alt text-xs"></i>
                      <span className="text-xs font-medium">
                        {service.warrantyPeriod || 90} Days Warranty till {new Date(Date.now() + ((service.warrantyPeriod || 90) * 24 * 60 * 60 * 1000)).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-4 md:px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                    <button
                      onClick={() => handleServiceClick(service)}
                      className="text-xs md:text-sm text-minto-600 hover:text-minto-700 font-medium flex items-center gap-1.5"
                    >
                      <i className="fas fa-info-circle"></i>
                      View Details
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (service) {
                          handleToggle(service);
                          if (!isSelected && isLoggedIn()) {
                            // @ts-ignore
                            window.gtag_report_conversion();
                          }
                        }
                      }}
                      className={`px-4 md:px-5 py-2 md:py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center min-w-[130px] md:min-w-[150px] ${
                        isSelected 
                          ? 'bg-red-50 text-red-500 hover:bg-red-100 active:bg-red-200 border border-red-200' 
                          : 'bg-minto-500 text-white hover:bg-minto-600 active:bg-minto-700 shadow-sm hover:shadow-md'
                      }`}
                    >
                      {isSelected ? (
                        <>
                          <i className="fas fa-trash-alt mr-1.5 text-xs"></i>
                          Remove
                        </>
                      ) : (
                        <>
                          <i className="fas fa-cart-plus mr-1.5 text-xs"></i>
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Reviews Section */}
        {reviews && reviews.length > 0 && (
          <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <i className="fas fa-star text-yellow-400 text-sm"></i>
              <h3 className="text-sm md:text-base font-semibold text-gray-800">Customer Reviews</h3>
            </div>
            <div className="p-4 mb-20">
              <ReviewList reviews={reviews} isLoading={isLoadingReviews} />
            </div>
          </div>
        )}
      </div>

      {selectedService && (
        <ServiceDetailsModal
          isOpen={!!selectedService}
          service={selectedService}
          onClose={() => setSelectedService(null)}
        />
      )}
    </>
  );
};

export default ServiceSelector; 